import { useState, useEffect, useCallback } from 'react';
import './Account.css';
import timezones from '../utils/timezones';
import { registerPasskey, passkeysSupported } from '../utils/webauthn';
import { listPasskeys, revokePasskey } from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';

// Helper to check if account is active - checks various possible field values
function isAccountActive(profile) {
  if (!profile) return false;

  // Check explicit active boolean
  if (profile.active === true) return true;

  // Check accountStatus field (case-insensitive)
  const status = profile.accountStatus?.toUpperCase?.();
  if (status === 'ACTIVE' || status === 'OK' || status === 'ENABLED') return true;

  // If user has email and is logged in, assume active (verified by being able to log in)
  if (profile.email && profile.emailVerified) return true;

  return false;
}

function Account({ userInfo, profile, onUpdateProfile }) {
  // Use profile if available, otherwise fall back to userInfo
  const displayProfile = profile || userInfo;

  const [formData, setFormData] = useState({
    name: displayProfile?.name || '',
    timezone: displayProfile?.timezone || 'UTC'
  });

  // ---- Passkeys (WebAuthn) ----
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState(null);

  const refreshPasskeys = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !passkeysSupported()) return;
    try {
      const resp = await listPasskeys(token);
      setPasskeys(resp?.credentials || []);
    } catch (e) {
      // Listing failure is non-fatal (e.g. WebAuthn disabled server-side)
      console.log('Passkey list unavailable:', e.message);
    }
  }, []);

  useEffect(() => { refreshPasskeys(); }, [refreshPasskeys]);

  const handleAddPasskey = async () => {
    setPasskeyMessage(null);
    setPasskeyBusy(true);
    try {
      const label = `${navigator.platform || 'Device'} — ${new Date().toLocaleDateString()}`;
      await registerPasskey(label, getAccessToken());
      setPasskeyMessage({ type: 'success', text: 'Passkey added. You can now sign in with it on this device.' });
      await refreshPasskeys();
    } catch (e) {
      if (e && e.name === 'NotAllowedError') {
        setPasskeyMessage(null); // user dismissed the prompt
      } else {
        setPasskeyMessage({ type: 'error', text: e.message || 'Could not add passkey' });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleRevokePasskey = async (credentialIdHex) => {
    if (!window.confirm('Remove this passkey? You will no longer be able to sign in with it.')) return;
    setPasskeyBusy(true);
    try {
      await revokePasskey(credentialIdHex, getAccessToken());
      await refreshPasskeys();
      setPasskeyMessage({ type: 'success', text: 'Passkey removed.' });
    } catch (e) {
      setPasskeyMessage({ type: 'error', text: e.message || 'Could not remove passkey' });
    } finally {
      setPasskeyBusy(false);
    }
  };

  const [hasChanges, setHasChanges] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // {type: 'success' | 'error', message: string}

  // Debug: Log profile data only when it changes (disabled for performance)
  // useEffect(() => {
  //   if (profile) {
  //     console.log('👤 Account component - profile updated:', profile);
  //   }
  // }, [profile]);

  // Update formData when profile changes
  useEffect(() => {
    setFormData({
      name: displayProfile?.name || '',
      timezone: displayProfile?.timezone || 'UTC'
    });
    setHasChanges(false);
    setStatusMessage(null); // Clear status message when profile updates
  }, [displayProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check if there are changes
    const isChanged =
      (name === 'name' && value !== displayProfile?.name) ||
      (name === 'timezone' && value !== displayProfile?.timezone) ||
      formData.name !== displayProfile?.name ||
      formData.timezone !== displayProfile?.timezone;

    setHasChanges(isChanged);
  };

  const handleSave = () => {
    const changes = {};
    if (formData.name !== displayProfile?.name) changes.name = formData.name;
    if (formData.timezone !== displayProfile?.timezone) changes.timezone = formData.timezone;

    if (Object.keys(changes).length > 0) {
      setStatusMessage({ type: 'info', message: 'Saving changes...' });
      onUpdateProfile(changes);
      setHasChanges(false);

      // Set success message after a delay (will be overridden by actual response in App.js)
      setTimeout(() => {
        setStatusMessage({ type: 'success', message: 'Profile updated successfully!' });
        setTimeout(() => setStatusMessage(null), 3000);
      }, 500);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="account-page">
      <div className="account-container">
        <div className="page-header">
          <h2 className="account-title">Account Information</h2>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </div>

        {statusMessage && (
          <div className={`status-message status-${statusMessage.type}`}>
            {statusMessage.message}
          </div>
        )}

        {!displayProfile ? (
          <div className="account-card">
            <div className="loading-message">Loading account information...</div>
          </div>
        ) : (
          <div className="account-card">
            <div className="account-section">
              <h3 className="section-title">Profile Details</h3>

              <div className="account-field">
                <label className="field-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="field-input"
                />
              </div>

              <div className="account-field">
                <label className="field-label">Email</label>
                <input
                  type="email"
                  value={displayProfile.email || ''}
                  disabled
                  className="field-input disabled-field"
                />
                <span className="field-note">Email cannot be changed</span>
              </div>

              <div className="account-field">
                <label className="field-label">Timezone</label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="field-input"
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="account-section">
              <h3 className="section-title">Account Details</h3>

              {displayProfile.createdAt && (
                <div className="account-field">
                  <label className="field-label">Account Created</label>
                  <div className="field-value">{formatDate(displayProfile.createdAt)}</div>
                </div>
              )}

              {displayProfile.lastLogin && (
                <div className="account-field">
                  <label className="field-label">Last Login</label>
                  <div className="field-value">{formatDate(displayProfile.lastLogin)}</div>
                </div>
              )}
            </div>

            {passkeysSupported() && (
              <div className="account-section">
                <h3 className="section-title">Passkeys</h3>
                <p className="field-note">
                  Sign in with Face ID, fingerprint or your device PIN instead of an email code.
                </p>

                {passkeyMessage && (
                  <div className={`status-message status-${passkeyMessage.type}`}>
                    {passkeyMessage.text}
                  </div>
                )}

                {passkeys.length > 0 && (
                  <div className="passkey-list">
                    {passkeys.map((pk) => (
                      <div key={pk.credentialIdHex} className="account-field passkey-row">
                        <div>
                          <div className="field-value">{pk.name || 'Passkey'}</div>
                          <span className="field-note">
                            Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString() : 'earlier'}
                            {pk.lastUsedAt ? ` - last used ${new Date(pk.lastUsedAt).toLocaleDateString()}` : ''}
                          </span>
                        </div>
                        <button
                          className="btn-revoke-passkey"
                          disabled={passkeyBusy}
                          onClick={() => handleRevokePasskey(pk.credentialIdHex)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button className="btn-save btn-add-passkey" onClick={handleAddPasskey} disabled={passkeyBusy}>
                  {passkeyBusy ? 'Working...' : 'Add a passkey on this device'}
                </button>
              </div>
            )}

            <div className="account-section">
              <h3 className="section-title">Account Status</h3>

              <div className="status-indicators">
                <div className={`account-status ${isAccountActive(displayProfile) ? 'active' : 'inactive'}`}>
                  <span className="status-dot"></span>
                  <span className="status-text">
                    {isAccountActive(displayProfile) ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {displayProfile.emailVerified !== undefined && (
                  <div className={`account-status ${displayProfile.emailVerified ? 'verified' : 'unverified'}`}>
                    <span className="status-dot"></span>
                    <span className="status-text">
                      {displayProfile.emailVerified ? 'Email Verified' : 'Email Not Verified'}
                    </span>
                  </div>
                )}

                {displayProfile.roles?.includes('ADMIN') && (
                  <div className="account-status admin">
                    <span className="status-dot"></span>
                    <span className="status-text">Administrator</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Account;